import { useState } from "react";
import { Plus, Edit2, Trash2, Search, FileText, Eye, X, Calendar, Flame, Tag, Clock, Image } from "lucide-react";
import RichEditor from "../RichEditor";
import { useAdmin } from "../admin-context";
import MediaPicker from "../MediaPicker";

const CATEGORIES = ["Recipes", "Culture", "Health", "Beverages", "News", "Community"];

const catColors = {
  Recipes:   "bg-orange-100 text-orange-600",
  Culture:   "bg-blue-100 text-blue-600",
  Health:    "bg-green-100 text-green-600",
  Beverages: "bg-amber-100 text-amber-700",
  News:      "bg-purple-100 text-purple-600",
  Community: "bg-pink-100 text-pink-600",
};

const initialForm = {
  title: "", cat: "Recipes", author: "", read: "", img: "",
  excerpt: "", content: "", tags: "", hot: false, status: "Draft",
};

const BlogAdmin = () => {
  const { blogPosts, addBlogPost, updateBlogPost, deleteBlogPost } = useAdmin();

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [isModalOpen, setIsModalOpen]   = useState(false);
  const [editingId, setEditingId]       = useState(null);
  const [formData, setFormData]         = useState(initialForm);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [previewPost, setPreviewPost]   = useState(null);
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);

  // ── CRUD handlers ────────────────────────────────────────────
  const handleOpen = (post = null) => {
    if (post) {
      setEditingId(post.id);
      setFormData({
        title: post.title, cat: post.cat, author: post.author,
        read: post.read || post.read_time || "", img: post.img, excerpt: post.excerpt,
        content: post.content || "", tags: Array.isArray(post.tags) ? post.tags.join(", ") : (post.tags || ""),
        hot: post.hot, status: post.status,
      });
    } else {
      setEditingId(null);
      setFormData(initialForm);
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      tags: formData.tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    if (editingId) {
      await updateBlogPost(editingId, payload);
    } else {
      await addBlogPost(payload);
    }
    setIsModalOpen(false);
  };

  const handleDelete = async (id) => {
    await deleteBlogPost(id);
    setDeleteConfirmId(null);
  };

  const field = (key, val) => setFormData((f) => ({ ...f, [key]: val }));

  // ── Filtering ────────────────────────────────────────────────
  const filtered = blogPosts.filter((p) => {
    const matchSearch = (p.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.author || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === "All" || p.status === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-8">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Blog Management</h1>
          <p className="text-gray-500 text-sm">Create and manage blog posts shown on the frontend.</p>
        </div>
        <button
          onClick={() => handleOpen()}
          className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-xl font-bold shadow-lg shadow-orange-200 flex items-center gap-2 transition active:scale-95"
        >
          <Plus size={20} /> New Post
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-5">
        {[
          { label: "Total Posts",  value: blogPosts.length,                                              color: "bg-orange-500", icon: FileText  },
          { label: "Published",    value: blogPosts.filter((p) => p.status === "Published").length,      color: "bg-green-500",  icon: Eye       },
          { label: "Drafts",       value: blogPosts.filter((p) => p.status === "Draft").length,          color: "bg-gray-400",   icon: Calendar  },
          { label: "Featured",     value: blogPosts.filter((p) => p.hot).length,                         color: "bg-red-400",    icon: Flame     },
        ].map((s) => (
          <div key={s.label} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
            <div className={`p-3 rounded-xl ${s.color}`}><s.icon size={20} className="text-white" /></div>
            <div>
              <p className="text-2xl font-extrabold text-gray-900">{s.value}</p>
              <p className="text-sm text-gray-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
          <h3 className="font-bold text-lg text-gray-800">All Posts</h3>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
              {["All", "Published", "Draft"].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                    filterStatus === s ? "bg-white shadow text-orange-600" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-56">
              <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search posts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-2 w-full border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left whitespace-nowrap">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-bold tracking-wider">
              <tr>
                <th className="p-5">Post</th>
                <th className="p-5">Category</th>
                <th className="p-5">Author</th>
                <th className="p-5">Tags</th>
                <th className="p-5">Status</th>
                <th className="p-5">Date</th>
                <th className="p-5">Views</th>
                <th className="p-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="p-10 text-center text-gray-400">No posts found.</td></tr>
              )}
              {filtered.map((post) => (
                <tr key={post.id} className="hover:bg-gray-50 transition">
                  <td className="p-5 max-w-[220px]">
                    <div className="flex items-center gap-3">
                      {post.img
                        ? <img src={post.img} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                        : <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0"><FileText size={16} className="text-orange-400" /></div>
                      }
                      <div className="min-w-0">
                        <p className="font-bold text-gray-900 truncate">{post.title}</p>
                        <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                          <Clock size={11} /> {post.read || post.read_time} read
                          {post.hot && <span className="ml-1 bg-red-100 text-red-500 px-1.5 py-0.5 rounded-full font-bold">HOT</span>}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="p-5">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${catColors[post.cat] || "bg-gray-100 text-gray-600"}`}>
                      {post.cat}
                    </span>
                  </td>
                  <td className="p-5 text-gray-600 text-sm">{post.author}</td>
                  <td className="p-5">
                    <div className="flex gap-1 flex-wrap max-w-[160px]">
                      {(post.tags || []).slice(0, 2).map((t) => (
                        <span key={t} className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">#{t}</span>
                      ))}
                      {(post.tags || []).length > 2 && <span className="text-[10px] text-gray-400">+{post.tags.length - 2}</span>}
                    </div>
                  </td>
                  <td className="p-5">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${
                      post.status === "Published"
                        ? "bg-green-100 text-green-700 border-green-200"
                        : "bg-gray-100 text-gray-500 border-gray-200"
                    }`}>
                      {post.status}
                    </span>
                  </td>
                  <td className="p-5 text-gray-500 text-sm">{post.created_at ? new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}</td>
                  <td className="p-5 font-bold text-gray-700">{(post.views || 0).toLocaleString()}</td>
                  <td className="p-5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setPreviewPost(post)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 transition" title="Preview">
                        <Eye size={16} />
                      </button>
                      <button onClick={() => handleOpen(post)} className="p-2 hover:bg-orange-50 rounded-lg text-orange-500 transition" title="Edit">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => setDeleteConfirmId(post.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-400 transition" title="Delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex justify-between items-center p-5 border-b border-gray-100">
              <h2 className="text-xl font-extrabold text-gray-900">{editingId ? "Edit Post" : "New Post"}</h2>
              <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition">
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">

              {/* Title */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Title</label>
                <input
                  required
                  value={formData.title}
                  onChange={(e) => field("title", e.target.value)}
                  placeholder="Post title..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                />
              </div>

              {/* Excerpt */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Excerpt <span className="text-gray-400 font-normal">(shown on blog cards)</span></label>
                <textarea
                  required
                  rows={3}
                  value={formData.excerpt}
                  onChange={(e) => field("excerpt", e.target.value)}
                  placeholder="Short description shown on the blog listing page..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition resize-none"
                />
              </div>

              {/* Content / Body */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">
                  Content <span className="text-gray-400 font-normal">(full article body)</span>
                </label>
                <RichEditor
                  value={formData.content}
                  onChange={(val) => field("content", val)}
                  placeholder="Write the full article content here..."
                />
              </div>

              {/* Cover Image */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Cover Image</label>
                <button
                  type="button"
                  onClick={() => setIsMediaPickerOpen(true)}
                  className="w-full border-2 border-dashed border-gray-200 hover:border-orange-400 rounded-xl p-3 flex items-center gap-3 transition text-left"
                >
                  {formData.img ? (
                    <img src={formData.img} alt="" className="w-14 h-14 object-cover rounded-lg flex-shrink-0 border" />
                  ) : (
                    <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <Image size={22} className="text-gray-400" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-gray-700">
                      {formData.img ? "Change cover image" : "Pick from Media Library"}
                    </p>
                    {formData.img && <p className="text-xs text-gray-400 truncate mt-0.5">{formData.img}</p>}
                  </div>
                  {formData.img && (
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); field("img", ""); }}
                      className="text-gray-300 hover:text-red-400 text-lg flex-shrink-0"
                      title="Clear"
                    >✕</button>
                  )}
                </button>
              </div>

              {/* Category + Author */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Category</label>
                  <select
                    value={formData.cat}
                    onChange={(e) => field("cat", e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 transition"
                  >
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Author</label>
                  <input
                    required
                    value={formData.author}
                    onChange={(e) => field("author", e.target.value)}
                    placeholder="Chef Name..."
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                  />
                </div>
              </div>

              {/* Read time + Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Read Time</label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-2.5 text-gray-400" size={16} />
                    <input
                      value={formData.read}
                      onChange={(e) => field("read", e.target.value)}
                      placeholder="5 min"
                      className="w-full pl-9 pr-4 border border-gray-200 rounded-xl py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => field("status", e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 transition"
                  >
                    <option>Draft</option>
                    <option>Published</option>
                  </select>
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">
                  Tags <span className="text-gray-400 font-normal">(comma separated)</span>
                </label>
                <div className="relative">
                  <Tag className="absolute left-3 top-2.5 text-gray-400" size={16} />
                  <input
                    value={formData.tags}
                    onChange={(e) => field("tags", e.target.value)}
                    placeholder="Dosa, Technique, Classic"
                    className="w-full pl-9 pr-4 border border-gray-200 rounded-xl py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                  />
                </div>
                {formData.tags && (
                  <div className="flex gap-1.5 flex-wrap mt-2">
                    {formData.tags.split(",").map((t) => t.trim()).filter(Boolean).map((t) => (
                      <span key={t} className="text-xs bg-orange-50 text-orange-600 px-2.5 py-1 rounded-full">#{t}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Featured toggle */}
              <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                <div>
                  <p className="text-sm font-bold text-gray-700 flex items-center gap-1.5"><Flame size={15} className="text-red-400" /> Featured / Hot Post</p>
                  <p className="text-xs text-gray-400 mt-0.5">Featured posts appear in the hero section on the blog page.</p>
                </div>
                <button
                  type="button"
                  onClick={() => field("hot", !formData.hot)}
                  className={`w-11 h-6 rounded-full transition-colors relative flex-shrink-0 ${formData.hot ? "bg-orange-500" : "bg-gray-300"}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.hot ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition">
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold shadow-lg shadow-orange-200 transition active:scale-95">
                  {editingId ? "Save Changes" : "Publish Post"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewPost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex justify-between items-center p-5 border-b border-gray-100">
              <h2 className="text-lg font-extrabold text-gray-900">Post Preview</h2>
              <button onClick={() => setPreviewPost(null)} className="p-2 hover:bg-gray-100 rounded-full transition">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {previewPost.img && (
                <img src={previewPost.img} alt={previewPost.title} className="w-full h-56 object-cover" />
              )}
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${catColors[previewPost.cat] || "bg-gray-100 text-gray-600"}`}>
                    {previewPost.cat}
                  </span>
                  {previewPost.hot && (
                    <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-red-100 text-red-500 flex items-center gap-1">
                      <Flame size={11} /> Featured
                    </span>
                  )}
                </div>
                <h1 className="text-2xl font-extrabold text-gray-900 leading-tight">{previewPost.title}</h1>
                <div className="flex items-center gap-3 text-sm text-gray-400">
                  <div className="w-7 h-7 bg-gradient-to-br from-orange-400 to-red-500 rounded-lg flex items-center justify-center text-white text-xs font-black">
                    {(previewPost.author || "?").split(" ").map((w) => w[0]).join("")}
                  </div>
                  <span className="font-medium text-gray-700">{previewPost.author}</span>
                  <span>·</span>
                  <span>{previewPost.created_at ? new Date(previewPost.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}</span>
                  <span>·</span>
                  <span className="flex items-center gap-1"><Clock size={12} /> {previewPost.read || previewPost.read_time} read</span>
                </div>
                <p className="text-gray-600 leading-relaxed">{previewPost.excerpt}</p>
                <div className="flex gap-2 flex-wrap pt-2">
                  {(previewPost.tags || []).map((t) => (
                    <span key={t} className="text-xs bg-orange-50 text-orange-600 px-3 py-1.5 rounded-full font-medium">#{t}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 size={26} className="text-red-500" />
            </div>
            <h2 className="text-lg font-extrabold text-gray-900">Delete Post?</h2>
            <p className="text-sm text-gray-500 mt-2">This action cannot be undone.</p>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setDeleteConfirmId(null)} className="flex-1 px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition">
                Cancel
              </button>
              <button onClick={() => handleDelete(deleteConfirmId)} className="flex-1 px-5 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold transition active:scale-95">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {isMediaPickerOpen && (
        <MediaPicker
          onSelect={({ url }) => { field("img", url); setIsMediaPickerOpen(false); }}
          onClose={() => setIsMediaPickerOpen(false)}
        />
      )}

    </div>
  );
};

export default BlogAdmin;
